/*
 * CaseRoutingCards — iter101.95
 *
 * A 3-card horizontal grid that surfaces the case's routing at a glance
 * (Planner · Production · Compliance Coach) AND owns every admin-side
 * assignment mutation. This is the canonical routing surface — the
 * legacy right-rail `TeamAndDeliveryPanel` has been retired and every
 * mutation that used to live there now lives here.
 *
 * State machine per card:
 *   Planner
 *     - self-plan            → N/A tile
 *     - assigned & accepted  → locked assignee row (no change)
 *     - assigned, unaccepted → assignee row + inline reassign dropdown
 *     - unassigned           → assign dropdown
 *
 *   Production
 *     - assigned             → locked assignee row
 *     - mfg_spec_pending     → dimmed "Waiting on planner"
 *     - pre-approval         → dimmed "Available after doctor approval"
 *     - blocked              → dimmed "Resolve blockers first"
 *     - ready                → assign dropdown → confirm modal → start production
 *
 *   Compliance Coach
 *     - not opted-in         → N/A tile
 *     - assigned             → assignee row + "Change" (unassign) button
 *     - opted-in, unassigned → assign dropdown (auto-assigns on pick)
 *
 * Design DNA — Invisique brand tokens only (`bg-primary`, `text-primary`).
 * No navy gradients. No purple. Icon disc is role-tinted (planner primary,
 * production emerald, coach amber) but every other affordance defaults
 * to the primary teal palette.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    ClipboardText, Wrench, HandHeart, ArrowRight, User, CheckCircle,
    XCircle, Lock, X, CircleNotch,
} from "@phosphor-icons/react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { confirmDialog } from "@/lib/confirm";


const AWAITING_TECH_ASSIGNMENT_STATUSES = new Set([
    "approved", "awaiting_production_assignment", "submitted_to_production",
]);
const MFG_SPEC_PENDING_STATUSES = new Set(["approved_pending_mfg"]);

const ROLE_TONES = {
    planner:    { discBg: "bg-primary/12",   discText: "text-primary"          },
    production: { discBg: "bg-emerald-100",  discText: "text-emerald-700"      },
    coach:      { discBg: "bg-amber-100",    discText: "text-amber-700"        },
};


/* ── primitives ────────────────────────────────────────────────────── */
function Initials({ name }) {
    if (!name) return "?";
    return name.split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase();
}

function Avatar({ name, image, size = 28 }) {
    if (image) {
        return (
            <img
                src={image}
                alt=""
                className="rounded-full object-cover ring-1 ring-border shrink-0"
                style={{ width: size, height: size }}
            />
        );
    }
    return (
        <div
            className="rounded-full grid place-items-center text-white font-bold shrink-0"
            style={{
                width: size,
                height: size,
                fontSize: size >= 32 ? 11 : 10,
                background: "linear-gradient(135deg, hsl(191 78% 32%), hsl(191 78% 40%))",
            }}
        >
            {Initials({ name })}
        </div>
    );
}

function StatusPill({ state }) {
    if (state === "assigned") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[9.5px] font-bold uppercase tracking-[0.1em] text-emerald-700">
                <CheckCircle size={9} weight="fill" /> Assigned
            </span>
        );
    }
    if (state === "n/a") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/40 border border-border text-[9.5px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                <XCircle size={9} weight="fill" /> N/A
            </span>
        );
    }
    if (state === "locked") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[9.5px] font-bold uppercase tracking-[0.1em] text-amber-700">
                <Lock size={9} weight="fill" /> Locked
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[9.5px] font-bold uppercase tracking-[0.1em] text-amber-700">
            <User size={9} weight="fill" /> Unassigned
        </span>
    );
}

/**
 * The card shell. Renders the icon disc, status pill, title/role
 * header, and hands the "body" slot to the parent (which decides
 * whether to render an assignee row, a select dropdown, etc.).
 */
function CardShell({ tone, state, Icon, title, role, testid, children }) {
    const isAssigned = state === "assigned";
    const isNa = state === "n/a";
    const isLocked = state === "locked";
    const t = ROLE_TONES[tone] || ROLE_TONES.planner;
    return (
        <div
            className={`relative rounded-2xl border overflow-hidden transition-all ${
                isAssigned || isLocked
                    ? "bg-gradient-to-br from-primary/5 to-primary/[0.02] border-primary/20"
                    : isNa
                        ? "bg-muted/20 border-dashed border-border opacity-60"
                        : "bg-card border-border hover:border-primary/30"
            }`}
            data-testid={testid}
        >
            {(isAssigned || isLocked) && (
                <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
            )}
            <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                    <div className={`w-10 h-10 rounded-xl grid place-items-center ${t.discBg}`}>
                        <Icon size={18} weight="duotone" className={t.discText} />
                    </div>
                    <StatusPill state={state} />
                </div>
                <div className="mb-3">
                    <div className="text-[13.5px] font-bold text-foreground leading-tight">{title}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{role}</div>
                </div>
                {children}
            </div>
        </div>
    );
}

/** Compact assignee row — 28px avatar + name + role sub. */
function AssigneeRow({ name, image, roleSub, right, testid }) {
    return (
        <div
            className="flex items-center gap-2.5 px-2.5 py-2 rounded-xl bg-white/70 border border-border"
            data-testid={testid}
        >
            <Avatar name={name} image={image} size={28} />
            <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-semibold text-foreground truncate">{name}</div>
                {roleSub && (
                    <div className="text-[10.5px] text-muted-foreground truncate">{roleSub}</div>
                )}
            </div>
            {right}
        </div>
    );
}

/** Simple "empty state" hint for N/A tiles. */
function NaHint({ children }) {
    return (
        <div className="text-[11.5px] text-muted-foreground italic px-2 py-1.5">
            {children}
        </div>
    );
}

/** Inline select — used by all three cards for assign/reassign. */
function AssignSelect({ value, onChange, placeholder, options, disabled, tone = "primary", testid }) {
    const border = tone === "danger"  ? "border-2 border-rose-300 bg-rose-50/40 text-rose-900 focus:ring-rose-300"
                 : tone === "success" ? "border-2 border-emerald-400 bg-emerald-50/30 text-emerald-900 focus:ring-emerald-400"
                                      : "border border-border bg-background focus:ring-primary/30";
    return (
        <select
            value={value}
            onChange={onChange}
            disabled={disabled}
            className={`w-full h-9 text-sm rounded-md px-2 font-medium focus:ring-2 focus:outline-none disabled:opacity-50 ${border}`}
            data-testid={testid}
        >
            <option value="">{placeholder}</option>
            {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
            ))}
        </select>
    );
}


/* ── Planner card ──────────────────────────────────────────────────── */
function PlannerCard({ c, planners, reload }) {
    const [saving, setSaving] = useState(false);
    const [reassignOpen, setReassignOpen] = useState(false);
    const isSelfPlan = !c?.planner_id && (c?.planning_mode || "").toLowerCase() === "doctor";
    const plannerLocked = Boolean(c?.planner_id) && !!c?.planner_accepted_at;
    const hasPlanner = Boolean(c?.planner_id);

    const state = isSelfPlan ? "n/a"
                : plannerLocked ? "locked"
                : hasPlanner ? "assigned"
                : "unassigned";

    const assign = async (id) => {
        if (!id) return;
        setSaving(true);
        try {
            await api.put(`/admin/cases/${c.id}/assign-planner`, { planner_id: id });
            toast.success("Planner assigned");
            setReassignOpen(false);
            await reload?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't assign planner");
        } finally {
            setSaving(false);
        }
    };

    let body;
    if (state === "n/a") {
        body = <NaHint>Not needed — doctor self-plans.</NaHint>;
    } else if (state === "locked") {
        body = (
            <AssigneeRow
                name={c.planner_name || "Planner"}
                roleSub="Planning Expert · Accepted"
                right={<Lock size={11} className="text-muted-foreground shrink-0" />}
                testid="routing-card-planner-assignee"
            />
        );
    } else if (state === "assigned") {
        body = (
            <div className="space-y-2">
                <AssigneeRow
                    name={c.planner_name || "Planner"}
                    roleSub="Planning Expert · Pending acceptance"
                    right={
                        <button
                            type="button"
                            onClick={() => setReassignOpen((v) => !v)}
                            className="text-[10px] text-primary hover:text-primary/80 inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-primary/5 transition shrink-0 font-semibold"
                            data-testid="routing-card-planner-change"
                        >
                            Change
                        </button>
                    }
                    testid="routing-card-planner-assignee"
                />
                {reassignOpen && (
                    <AssignSelect
                        value=""
                        onChange={(e) => assign(e.target.value)}
                        disabled={saving}
                        placeholder="Reassign to another planner…"
                        options={planners.filter((p) => p.id !== c.planner_id).map((p) => ({
                            value: p.id,
                            label: `${p.name}${typeof p.active_case_count === "number" ? ` · ${p.active_case_count} active` : ""}`,
                        }))}
                        testid="ops-planner-select"
                    />
                )}
            </div>
        );
    } else {
        body = (
            <AssignSelect
                value=""
                onChange={(e) => assign(e.target.value)}
                disabled={saving}
                placeholder="Assign a planner…"
                options={planners.map((p) => ({
                    value: p.id,
                    label: `${p.name}${typeof p.active_case_count === "number" ? ` · ${p.active_case_count} active` : ""}`,
                }))}
                testid="ops-planner-select"
            />
        );
    }
    return (
        <CardShell
            tone="planner"
            state={state}
            Icon={ClipboardText}
            title="Planner"
            role="Treatment Planning"
            testid="routing-card-planner"
        >
            {body}
        </CardShell>
    );
}


/* ── Production card ───────────────────────────────────────────────── */
function ProductionCard({ c, productionTechs, techBlocked, onPickPending }) {
    if (c?.production_tech_id) {
        return (
            <CardShell
                tone="production"
                state="assigned"
                Icon={Wrench}
                title="Production"
                role="Manufacturing"
                testid="routing-card-production"
            >
                <AssigneeRow
                    name={c.production_tech_name || "Production tech"}
                    roleSub="Production Tech"
                    right={<CheckCircle size={11} weight="fill" className="text-emerald-700 shrink-0" />}
                    testid="routing-card-production-assignee"
                />
            </CardShell>
        );
    }
    if (MFG_SPEC_PENDING_STATUSES.has(c?.status)) {
        return (
            <CardShell
                tone="production"
                state="n/a"
                Icon={Wrench}
                title="Production"
                role="Manufacturing"
                testid="routing-card-production"
            >
                <NaHint>Waiting for planner — MFG spec pending.</NaHint>
            </CardShell>
        );
    }
    if (!AWAITING_TECH_ASSIGNMENT_STATUSES.has(c?.status)) {
        return (
            <CardShell
                tone="production"
                state="n/a"
                Icon={Wrench}
                title="Production"
                role="Manufacturing"
                testid="routing-card-production"
            >
                <NaHint>Available after doctor approval.</NaHint>
            </CardShell>
        );
    }
    if (techBlocked) {
        return (
            <CardShell
                tone="production"
                state="n/a"
                Icon={Wrench}
                title="Production"
                role="Manufacturing"
                testid="routing-card-production"
            >
                <NaHint>Resolve blockers above to enable.</NaHint>
            </CardShell>
        );
    }
    return (
        <CardShell
            tone="production"
            state="unassigned"
            Icon={Wrench}
            title="Production"
            role="Manufacturing"
            testid="routing-card-production"
        >
            <AssignSelect
                value=""
                onChange={(e) => e.target.value && onPickPending(e.target.value)}
                placeholder="Pick a tech — starts production…"
                options={productionTechs.map((t) => ({
                    value: t.id,
                    label: `${t.name}${typeof t.active_case_count === "number" ? ` · ${t.active_case_count} active` : ""}`,
                }))}
                tone="success"
                testid="ops-production-select"
            />
        </CardShell>
    );
}


/* ── Compliance Coach card ─────────────────────────────────────────── */
function CoachCard({ c, reload }) {
    const isOptedIn = Boolean(c?.coe_support_compliance);
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(isOptedIn);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isOptedIn) { setLoading(false); return () => {}; }
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/admin/coe-compliance/staff");
                if (!cancelled) setStaff(r.data || []);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isOptedIn]);

    if (!isOptedIn) {
        return (
            <CardShell
                tone="coach"
                state="n/a"
                Icon={HandHeart}
                title="Compliance Coach"
                role="Wear-time coaching"
                testid="routing-card-coach"
            >
                <NaHint>Not needed — doctor manages compliance.</NaHint>
            </CardShell>
        );
    }

    const currentAssigneeId = c.coe_support_assigned_to || "";
    const currentAssignee = staff.find((s) => s.id === currentAssigneeId);

    const doAssign = async (id) => {
        if (!id) return;
        setSaving(true);
        try {
            await api.post(`/admin/coe-compliance/cases/${c.id}/assign`, { coe_user_id: id });
            toast.success("Compliance coach assigned");
            await reload?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not assign");
        } finally {
            setSaving(false);
        }
    };
    const doUnassign = async () => {
        const ok = await confirmDialog({
            title: "Unassign compliance coach?",
            description: `Removes ${currentAssignee?.name || "the current coach"} from this case. The doctor's CoE-Support opt-in stays — you can reassign any time.`,
            confirmText: "Unassign",
            destructive: true,
        });
        if (!ok) return;
        setSaving(true);
        try {
            await api.post(`/admin/coe-compliance/cases/${c.id}/unassign`);
            toast.success("Coach unassigned");
            await reload?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not unassign");
        } finally {
            setSaving(false);
        }
    };

    return (
        <CardShell
            tone="coach"
            state={currentAssignee ? "assigned" : "unassigned"}
            Icon={HandHeart}
            title="Compliance Coach"
            role="Wear-time coaching"
            testid="routing-card-coach"
        >
            {currentAssignee ? (
                <AssigneeRow
                    name={currentAssignee.name}
                    image={currentAssignee.image}
                    roleSub={currentAssignee.staff_type || "CoE-Support"}
                    right={
                        <button
                            type="button"
                            onClick={doUnassign}
                            disabled={saving}
                            className="text-[10px] text-rose-600 hover:text-rose-700 inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-rose-50 transition disabled:opacity-50 shrink-0"
                            data-testid="compliance-unassign-btn"
                        >
                            <X size={10} weight="bold" /> Change
                        </button>
                    }
                    testid="compliance-current-assignee"
                />
            ) : loading ? (
                <div className="w-full h-9 rounded-md border border-dashed border-border bg-muted/20 text-sm flex items-center justify-center text-muted-foreground gap-1.5">
                    <CircleNotch size={12} className="animate-spin" /> Loading staff…
                </div>
            ) : (
                <AssignSelect
                    value=""
                    onChange={(e) => doAssign(e.target.value)}
                    disabled={saving}
                    placeholder="Assign a compliance coach…"
                    options={staff.map((s) => ({
                        value: s.id,
                        label: `${s.name} · ${s.staff_type || "staff"}`,
                    }))}
                    tone="danger"
                    testid="compliance-assign-select"
                />
            )}
        </CardShell>
    );
}


/* ── Main export ───────────────────────────────────────────────────── */
/**
 * The 3-card grid + the confirmation modal that fires when the admin
 * picks a production technician. The confirmation is scoped here (not
 * inside `ProductionCard`) so it can also reference the picked tech's
 * name across a state boundary.
 */
export function CaseRoutingCards({ c, blockers, planners, productionTechs, reload }) {
    const [pendingTechId, setPendingTechId] = useState(null);
    const pendingTech = productionTechs.find((t) => t.id === pendingTechId);
    const techBlocked = (blockers || []).length > 0;

    const confirmProduction = async () => {
        const id = pendingTechId;
        setPendingTechId(null);
        try {
            await api.put(`/admin/cases/${c.id}/assign-production`, { production_tech_id: id });
            toast.success("Production technician assigned");
            await reload?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    return (
        <>
            <div
                className="rounded-2xl border border-border bg-card overflow-hidden"
                data-testid="case-routing-cards"
            >
                <div className="px-5 py-3 border-b border-border bg-secondary/30">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                        Case Routing
                    </div>
                </div>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <PlannerCard c={c} planners={planners} reload={reload} />
                    <ProductionCard
                        c={c}
                        productionTechs={productionTechs}
                        techBlocked={techBlocked}
                        onPickPending={setPendingTechId}
                    />
                    <CoachCard c={c} reload={reload} />
                </div>
            </div>

            {/* Production tech assignment confirmation modal — moved here from
                the retired TeamAndDeliveryPanel. */}
            <Dialog open={!!pendingTechId} onOpenChange={(o) => !o && setPendingTechId(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Send to production?</DialogTitle>
                        <DialogDescription>
                            Assigning <span className="font-bold text-foreground">{pendingTech?.name || "this technician"}</span> as Production Technician will move this case to <span className="font-bold text-foreground">IN PRODUCTION</span>. This kicks off the manufacturing workflow and is logged in the audit trail.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPendingTechId(null)} data-testid="confirm-production-cancel">Cancel</Button>
                        <Button
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={confirmProduction}
                            data-testid="confirm-production-submit"
                        >
                            Confirm — start production
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
